import { ProductDocument } from '../containers/products/types';
import { OrderStatus2, Currencies, FeeType } from '../types';
import {api} from './api';

interface ShippingInfo {
	email: string,
	phone: string,
	firstName: string,
	lastName: string,
	addressLine1: string,
	addressLine2: string,
	postalCode: string,
	city: string,
	state: string,
	country: string
}

export interface PendingOrderProduct {
	id: number;
	quantity: string;
	name: string;
	sku: string;
	description: string;
	pricePerUnit: string;
	uom: string;
	imgUrl?: string;
	digitalDelivery?: boolean;
	documentsCount?: number;
  }

export interface PaidOrderProduct extends PendingOrderProduct {
	documents: ProductDocument[];
}
  
export interface PendingOrderAdditionalFee {
	id: number;
	name: string;
	price: string;
	description?: string;
	type: FeeType;
  }
  
export interface PreviewOrder {
	id: number;
	planId: number;
	products: PendingOrderProduct[];
	additionalFees: PendingOrderAdditionalFee[];
	total: number;
	currency: Currencies;
	vendorName: string;
	vendorAddress: string;
	vendorTaxId: string;
	combinedPaymentTotal: number | null;
	isEligibleForDigitalDelivery: boolean;
  }
  
export interface PendingOrder extends PreviewOrder {
	status: OrderStatus2;
	customerFirstName: string;
	customerLastName: string;
	customerEmail: string;
	phone: string;
	customerAddress1: string;
	customerAddress2: string;
	customerCity: string;
	customerState: string;
	customerPostalCode: string;
	customerCountry: string;
	shippingMethod: string;
	shippingMethodId: number;
	paymentIntentId: string;
	paymentIntentClientSecret: string;
  }

export type PaidOrder = PendingOrder;

export const ordersApi = api.injectEndpoints({
	endpoints: (builder) => ({
		orderListLoad: builder.mutation({
			query: () => ({
				url: 'orders/list',
				method: 'GET',
			}),
		}),
		orderDetailsLoad: builder.mutation({
			query: (orderId: string) => ({
				url: `orders/${orderId}/details`,
				method: 'GET',
			}),
		}),
		orderGetInfo: builder.query({
			query: (orderId: string) => ({
				url: `orders/${orderId}/info`,
				method: 'GET',
			}),
		}),
		orderCancel: builder.mutation({
			query: (orderId: string) => ({
				url: `orders/${orderId}/cancel`,
				method: 'POST',
			}),
		}),
		orderMockPaymentCompleted: builder.mutation({
			query: (orderId: string) => ({
				url: `orders/${orderId}/paymentCompleted`,
				method: 'POST',
			}),
		}),
		retrievePendingOrder: builder.query<{order: PendingOrder, combinedOrder?: PendingOrder | null}, {paymentIntentId: string, clientSecret: string}>({
			query: ({paymentIntentId, clientSecret}) => ({
				url: `/orders/retrievePendingOrder?paymentIntentId=${paymentIntentId}&clientSecret=${clientSecret}`,
				method: 'GET',
			}),
		}),
		retrievePaidOrder: builder.query<{order: PaidOrder, combinedOrder?: PaidOrder | null}, {paymentIntentId: string, clientSecret: string}>({
			query: ({paymentIntentId, clientSecret}) => ({
				url: `/orders/retrievePaidOrder?paymentIntentId=${paymentIntentId}&clientSecret=${clientSecret}`,
				method: 'GET',
			}),
		}),
		submitShippingInfo: builder.mutation<void, { shippingInfo: ShippingInfo, paymentIntentId: string, clientSecret: string }>({
			query: ({shippingInfo, paymentIntentId, clientSecret}) => ({
				url: `/orders/submitShippingInfo?paymentIntentId=${paymentIntentId}&clientSecret=${clientSecret}`,
				method: 'POST',
				body: shippingInfo,
			}),
		}),
	}),
});

export const {useOrderCancelMutation, useOrderListLoadMutation, useOrderDetailsLoadMutation, useOrderGetInfoQuery, useOrderMockPaymentCompletedMutation, useRetrievePendingOrderQuery, useRetrievePaidOrderQuery, useSubmitShippingInfoMutation} = ordersApi;
